import {
  Building2,
  MapPin,
  Calendar,
  DollarSign,
  Clock,
  User,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import type { PurchaseRequest } from '../../types';

interface DetailsTabProps {readonly request: PurchaseRequest;}

export function DetailsTab({ request }: DetailsTabProps) {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: request.currency,
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Request Information</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Site</p>
                <p className="font-medium">{request.siteName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Delivery Zone</p>
                <p className="font-medium">{request.deliveryZone}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Required By</p>
                <p className="font-medium">{formatDate(request.requiredByDate)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Estimated Total</p>
                <p className="font-medium text-lg">{formatCurrency(request.estimatedTotal)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Requester Information</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Requested By</p>
                <p className="font-medium">{request.createdByName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Created At</p>
                <p className="font-medium">{formatDate(request.createdAt)}</p>
              </div>
            </div>
          </div>
          {request.description && (
            <div className="mt-4">
              <p className="text-sm text-gray-500">Description</p>
              <p className="mt-1 text-gray-700">{request.description}</p>
            </div>
          )}
        </div>
      </div>

      {request.status === 'approved' && request.approvedByName && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
            <div>
              <p className="font-medium text-green-800">Approved by {request.approvedByName}</p>
              {request.approvedAt && (
                <p className="text-sm text-green-700">{formatDate(request.approvedAt)}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {request.status === 'rejected' && request.rejectionReason && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
          <div className="flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Rejection Reason</p>
              <p className="text-sm text-red-700">{request.rejectionReason}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
